import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import noLogEntriesPng from '@/assets/illustrations/empty/no-log-entries.png';

import { useActivePropertyStore } from '@/stores/active-property-store';
import { useLogEntryStore } from '@/stores/log-entry-store';
import { usePropertyStore } from '@/stores/property-store';
import { PropertyPill } from '@/components/map/PropertyPill';
import { LogTimeline } from '@/components/scouting/LogTimeline';
import { OperationalGuide } from '@/components/common/OperationalGuide';

export function ScoutLogPage() {
  const navigate = useNavigate();
  const { activePropertyId, setActiveProperty } = useActivePropertyStore();
  const { entries, loadEntriesByProperty } = useLogEntryStore();
  const { properties, loadProperties } = usePropertyStore();

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  // M-07: Auto-select the primary property when no property is active and
  // a primary property exists in the list. (Replaces the old defaultPropertyId
  // path through UserPreferences.)
  useEffect(() => {
    if (!activePropertyId) {
      const primary = properties.find((p) => p.isPrimary);
      if (primary) {
        setActiveProperty(primary.id);
      }
    }
  }, [activePropertyId, properties, setActiveProperty]);

  useEffect(() => {
    if (activePropertyId) {
      void loadEntriesByProperty(activePropertyId);
    }
  }, [activePropertyId, loadEntriesByProperty]);

  function handleEntryClick(id: string) {
    const entry = entries.find((e) => e.id === id);
    if (entry) {
      navigate(`/waypoints/${entry.waypointId}/log/${entry.id}`);
    }
  }

  function handleAddClick() {
    navigate('/scout/log/new');
  }

  // ── State 1: No properties at all ────────────────────────────────────────
  if (properties.length === 0) {
    return (
      <div data-testid="scout-log-no-property">
        <OperationalGuide
          heading="Field Log is Empty"
          subtext="Log a scout to start tracking movement patterns and atmospheric data."
          illustration={noLogEntriesPng}
          ctaLabel="Add your first property"
          ctaLink="/land/properties/new"
          data-guide-id="scout-log-no-property"
        />
      </div>
    );
  }

  // ── State 2: Properties exist but no active property (auto-select pending or no default) ──
  if (!activePropertyId) {
    return (
      <div data-testid="scout-log-select-property">
        <OperationalGuide
          illustration={noLogEntriesPng}
          heading="Field Log is Empty"
          subtext="Select a property and log your first scout to start tracking movement patterns."
          ctaLabel="Log a Scout"
          ctaLink="/scout/log/new"
          data-guide-id="scout-log-select-property"
        />
      </div>
    );
  }

  // ── States 3 & 4: Active property set — show log (or no-entries OperationalGuide via LogTimeline) ──
  const propertyEntries = entries.filter((e) => e.propertyId === activePropertyId);
  const activeProperty = properties.find((p) => p.id === activePropertyId);

  return (
    <div className="relative flex flex-col gap-4" data-testid="scout-log-page">
      {/* Context header — shows which property is active.
          When the user has multiple properties, render only the inline pill
          so they can switch without leaving the page (M-06: removed the
          standalone field-label that duplicated the property name). */}
      {properties.length > 1 && activeProperty ? (
        <div className="flex items-center gap-2" data-testid="scout-context-header">
          <PropertyPill
            properties={properties}
            selectedProperty={activeProperty}
            onSelect={setActiveProperty}
          />
        </div>
      ) : (
        <p className="field-label" data-testid="scout-context-header">
          {activeProperty?.name ?? 'Scouting Log'}
        </p>
      )}

      <LogTimeline
        entries={propertyEntries}
        onEntryClick={handleEntryClick}
        onAddClick={handleAddClick}
      />

      {/* FAB — bottom-right; only rendered when there are entries (empty state CTA is the primary action) */}
      {propertyEntries.length > 0 && (
        <button
          type="button"
          onClick={handleAddClick}
          className="fixed bottom-20 right-4 z-10 w-14 h-14 rounded-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-hover)] shadow-lg flex items-center justify-center transition-colors"
          aria-label="Add scouting entry"
          data-testid="scout-log-fab"
          style={{ bottom: 'calc(56px + env(safe-area-inset-bottom) + 16px)' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
    </div>
  );
}
